import { digitStateAt, REVEAL_WINDOW_MS } from './revealTimeline.ts';

/**
 * 开奖台自己的落定窗口，**比共用的 REVEAL_WINDOW_MS(5.4s) 慢**。
 *
 * <p>5.4s 里塞 4 位是每位 1.2 秒 —— 快到「还没看清就下一位了」（2026-08-20 真机
 * 反馈）。这一段是整个玩法情绪的顶点，值得多给两秒：每落定一位，还在场的人就少
 * 一个数量级，那种收紧的感觉需要时间才生得出来。
 *
 * <p>没有直接改 REVEAL_WINDOW_MS：那个是四个游戏共用的节奏（PRODUCT_NOTES §0.1），
 * 而开奖台是 Milhar 独有的舞台，慢下来只影响它自己。
 */
export const STAGE_REVEAL_MS = 9_000;

/**
 * 开奖闸门的展示状态。
 *
 * <p><b>硬规则</b>：上一期没有开出结果，就不接这一期的单。玩家必须先看到上一次开的是
 * 什么，才轮到他决定下一次押什么。原来这两件事同时成立 —— 屏幕一边说「上一期还在开」，
 * 一边显示 `CLOSES 4:44` 邀请你下注 —— 那正是追号玩法最不该给的暗示。
 *
 * <p><b>闸门的真身在后端</b>（`PREV_ROUND_DRAWING`，与房间玩法同一条）。这里只负责让
 * 界面不去邀请一个必然被拒的动作，并把这段等待演出来：1/3 → 2/3 → 3/3 → Computing，
 * 与末尾彩的观感一致。
 *
 * <p>区块节奏在本地算而不是问服务端：状态轮询 5 秒一次，扛不住 12 秒一格的动画。
 * 但**结果何时公开仍以服务端为准** —— 后端把 `drawing` 拿掉，这块就立刻消失，
 * 本地计时器说了不算。
 */

export type DrawingGate = {
  /** 正在开奖的那一期（上一期）。 */
  round: number;
  /** 那一期的结束时刻，秒。区块节奏从这里起算。 */
  endsAt: number;
  /** 安全阀：超过这么久无论如何放行，见后端 prevResultGraceSec。 */
  graceSec: number;
};

/** 每个区块的秒数。与房间玩法同值 —— 两个玩法的等待观感不该有差别。 */
export const GATE_BLOCK_SEC = 12;

/** 区块总数。 */
export const GATE_BLOCKS = 3;

export type GateView = {
  /** 已经确认的区块数，0..3。 */
  confirmed: number;
  /** 三个区块都过了，进入「在算了」。 */
  computing: boolean;
  /** 药丸里的文字：`1/3` 这种，或 computing 时的 `···`。 */
  pill: string;
};

/**
 * 闸门此刻该显示成什么样。
 *
 * @param gate 后端给的 drawing 块；没有就是没闸住
 * @param nowSec 当前时刻，秒
 * @returns 没闸住时 null —— 调用方据此回到正常倒计时
 */
export function drawGateView(
  gate: DrawingGate | null | undefined,
  nowSec: number,
): GateView | null {
  if (!gate || !Number.isFinite(gate.endsAt) || !Number.isFinite(nowSec)) return null;
  const elapsed = nowSec - gate.endsAt;
  // 上一期还没走完（时钟漂移，或恰好卡在边界）：还没到演出时间
  if (elapsed < 0) return null;
  const confirmed = Math.min(GATE_BLOCKS, Math.floor(elapsed / GATE_BLOCK_SEC));
  const computing = confirmed >= GATE_BLOCKS;
  return {
    confirmed,
    computing,
    // 演出到 3/3 之后不再报进度 —— 再往后是真的在等后端，给个确定的数字反而是撒谎
    pill: computing ? '···' : `${confirmed + 1}/${GATE_BLOCKS}`,
  };
}

/**
 * 本地是否该自行放弃等待。
 *
 * <p>正常情况下后端撤掉 `drawing` 就结束了。这条只对付**后端还在给但已经超过安全阀**
 * 的情况（开奖服务卡死）：界面不能永远停在 Computing，否则玩家看到的是一个死掉的游戏。
 */
export function gateExpired(gate: DrawingGate | null | undefined, nowSec: number): boolean {
  if (!gate || !Number.isFinite(gate.graceSec)) return false;
  return nowSec - gate.endsAt >= gate.graceSec;
}

/**
 * 出结果之后要定格多久，秒。
 *
 * <p>闸门放开的那一刻如果直接回到下一期的倒计时，玩家的体验是：等了三格 + Computing，
 * 然后**什么都没发生**，画面直接开始下一局。他等的那个东西从来没有出现在他眼前
 * （它只是悄悄进了页面下方那一排卡片）。这一段等待因此变成纯粹的空耗。
 *
 * <p>8 秒：够读完 4-5 位数字并反应过来，又不至于长到挡住下一期还剩多久。定格期间
 * **下单是开放的** —— 闸门已经放行了，规则要的是「先看到上一次开的是什么」，
 * 而这正是他此刻正在看的。
 */
export const RESULT_HOLD_SEC = 8;

export type HeldResult = { round: number; result: string };

/**
 * 闸门刚放开时，把那一期的结果从最近结果里捞出来。
 *
 * <p>两者来自**同一个状态响应**，所以不会出现「闸门放了但结果还没到」的错拍。
 * 捞不到就返回 null —— 安全阀提前放行（开奖服务卡住）时就是这种情况，
 * 此时不该定格一个不存在的结果，直接回倒计时。
 */
export function justDrawn(
  gateRound: number | null | undefined,
  results: readonly { round: number; result: string | null }[] | undefined,
): HeldResult | null {
  if (!gateRound || !results) return null;
  const hit = results.find((r) => r.round === gateRound && !!r.result);
  return hit && hit.result ? { round: hit.round, result: hit.result } : null;
}

/**
 * 定格还在不在。
 *
 * @param held 捞到结果的那一刻记下的 `{round, result, atSec}`
 */
export function heldResultView(
  held: (HeldResult & { atSec: number }) | null | undefined,
  nowSec: number,
  holdSec = RESULT_HOLD_SEC,
): HeldResult | null {
  if (!held || !Number.isFinite(held.atSec) || !Number.isFinite(nowSec)) return null;
  if (nowSec - held.atSec >= holdSec) return null;
  return { round: held.round, result: held.result };
}

export type HeldDrawCapture = {
  round: number;
  result: string;
  /** 服务端校正过的秒钟（nowSec），**不是** Date.now()。 */
  atSec: number;
  /** 单调时钟（performance.now），逐位落定用。 */
  atMs: number;
  mine?: boolean;
  payout?: string | null;
};

/**
 * 定格一期结果时记下的快照。
 *
 * <p><b>atSec 必须传服务端校正过的 `nowSec`，绝不能用原始 `Date.now()`。</b>定格还在不在
 * 由 {@link heldResultView} 用**同一个 nowSec** 判定（`nowSec - atSec >= holdSec` 就撤），
 * 两个时钟一旦不同源，手机比服务端慢十几秒就会让 `nowSec - atSec` 在定格一诞生时
 * 就 ≥ holdSec —— 结果一格没亮就被判过期，整段逐位落定 + 定格演出直接跳过。
 * 这正是 `clockSkew.ts` 头注写的那个失败（「手机快 30 秒……整段开奖演出根本不会发生」），
 * 只是方向相反：手机**慢**则演出被跳过。atMs 走单调时钟，只管落定动画，不受此影响。
 */
export function captureHeldDraw(
  drawn: HeldResult,
  now: { sec: number; ms: number },
  extra?: { mine?: boolean; payout?: string | null },
): HeldDrawCapture {
  return {
    round: drawn.round,
    result: drawn.result,
    atSec: now.sec,
    atMs: now.ms,
    mine: extra?.mine,
    payout: extra?.payout ?? null,
  };
}

/** 常态轮询间隔。 */
export const POLL_IDLE_MS = 5_000;

/** Computing 期间的轮询间隔。 */
export const POLL_COMPUTING_MS = 1_000;

/**
 * 此刻该多久问一次状态。
 *
 * <p><b>「Computing 到出结果」那几秒，大部分不是在开奖，是在等下一次轮询。</b>
 * 三个区块走完是封盘后 36 秒（12s × 3），而后端实际开出结果是 34-38 秒 ——
 * 两者本来就贴着。真正的延迟来自 5 秒一次的轮询：结果在服务端已经有了，
 * 客户端还没去问。最坏情况 38 − 36 + 5 ≈ 7 秒，其中 5 秒纯属间隔。
 *
 * <p>所以**只在 Computing 期间提速到 1 秒**，把最坏情况压到 ~3 秒。
 *
 * <p>为什么不在整个闸门期间都提速：闸门从封盘一直持续到开奖公开（约 35-40 秒），
 * 全程 1 秒会把这段窗口的请求量抬到 5 倍，而前 36 秒里结果**根本不可能**已经存在
 * —— 那段提速买不到任何东西。Computing 才是「结果随时可能到」的那一段。
 */
export function gatePollMs(gate: GateView | null): number {
  return gate?.computing ? POLL_COMPUTING_MS : POLL_IDLE_MS;
}

/**
 * 定格总时长 = 逐位落定的窗口 + 落定后还能看几秒。
 *
 * <p>数字不再一次性亮出来，而是**从最后一位开始逐位落定**（复用 revealTimeline 的
 * 时序，与个人开奖卡同一套节奏，不另起一套）。落定过程本身要 REVEAL_WINDOW_MS，
 * 所以定格得比原来长 —— 否则数字刚凑齐就被倒计时收走，等于没让人看清。
 */
export const RESULT_SETTLE_SEC = 4;
/** 定格总时长 = 开奖台的落定窗口 + 落定后还能看几秒。跟着 STAGE_REVEAL_MS 走。 */
export const RESULT_TOTAL_HOLD_SEC = Math.ceil(STAGE_REVEAL_MS / 1000) + RESULT_SETTLE_SEC;

export type GateCell = {
  /** 已落定的那一位；还没落定时为 null（渲染成 `–`）。 */
  char: string | null;
  /** `next` 是下一个要落的位 —— 它在闪，于是不用读文字也知道正在开哪一位。 */
  state: 'settled' | 'next' | 'pending';
};

/**
 * 每一位此刻该显示什么。
 *
 * <p><b>从最后一位开始落</b>。这个顺序不是装饰：奖级规则就是从右往左比，每落定一位，
 * 还在场的人就少一个数量级（9% → 0,9% → 0,09% → 0,01%）。顺序反过来，紧张感就没了 ——
 * 先开最高位的话，绝大多数人在第一格就已经出局，后面三格与他无关。
 *
 * @param result 已开出的号；`null` 表示还在 Computing（全是 `–`，最后一位在闪）
 * @param elapsedMs 从结果到手起算的毫秒
 */
export function gateCells(
  digits: number,
  result: string | null | undefined,
  elapsedMs: number,
): GateCell[] {
  const n = Math.max(0, Math.floor(digits));
  if (!n) return [];
  const chars = result ? result.split('') : [];
  // 把开奖台的慢节奏折算回共用时序：等价于把同一条曲线拉长到 STAGE_REVEAL_MS，
  // 落定顺序与间隔比例完全不变，只是整体变慢。
  const scaled = elapsedMs * (REVEAL_WINDOW_MS / STAGE_REVEAL_MS);
  const settled = (i: number) =>
    !!result && chars.length === n && digitStateAt(scaled, i, n) === 'settled';
  // 下一个要落的 = 还没落定里下标最大的那个（落定顺序是 n-1 → 0）
  let next = -1;
  for (let i = n - 1; i >= 0; i -= 1) {
    if (!settled(i)) { next = i; break; }
  }
  return Array.from({ length: n }, (_, i) => ({
    char: settled(i) ? chars[i] : null,
    state: settled(i) ? 'settled' as const : i === next ? 'next' as const : 'pending' as const,
  }));
}

/** 全部落定了没有。落定后才该出中奖/未中的反馈，否则等于提前剧透。 */
export function gateCellsComplete(cells: readonly GateCell[]): boolean {
  return cells.length > 0 && cells.every((c) => c.state === 'settled');
}

/** 闸门关掉之后，最多再等多久去捞结果。 */
export const RESULT_WAIT_MS = 20_000;

export type CaptureDecision = 'capture' | 'wait' | 'giveup';

/**
 * 闸门刚关，这一期的结果该不该现在捞。
 *
 * <p><b>这是 2026-08-19 真机复现的那个 bug 的修复点。</b>闸门有两种关法：后端撤掉
 * `drawing`（结果到了），或者本地安全阀 {@link gateExpired} 到点（结果还没到）。
 * 原来的实现两种都当成「该捞结果了」，捞不到就把记着的期号清掉 —— 于是开奖比
 * 50 秒慢一点点时，机会被烧掉一次，几秒后结果真的到了也永远不会再触发。
 * 屏幕上的表现就是：Computing 之后什么都没有，直接跳下一期。
 *
 * <p>所以捞不到时返回 `wait`，**保留期号等下一次轮询**；只有等过了
 * {@link RESULT_WAIT_MS} 还没有，才认输 —— 那种情况多半是这一期被作废了。
 */
export function captureDecision(
  pendingRound: number | null | undefined,
  results: readonly { round: number; result: string | null }[] | undefined,
  waitedMs: number,
): CaptureDecision {
  if (!pendingRound) return 'giveup';
  if (justDrawn(pendingRound, results)) return 'capture';
  return waitedMs >= RESULT_WAIT_MS ? 'giveup' : 'wait';
}

/**
 * 那一格现在该不该闪。
 *
 * <p><b>三格走完之前不闪。</b>闪是「下一个就是它了」的信号，而 1/3、2/3 那 24 秒里
 * 什么都还没到 —— 从头闪到尾等于在没有事情发生的时候一直催，等真的要落定时
 * 这个信号已经不新鲜了（2026-08-20 评审）。
 *
 * <p>定格阶段（gate 已关、正在逐位落定）当然要闪：那时每一格都真的马上要落。
 */
export function cellsArmed(gate: GateView | null | undefined): boolean {
  return !gate || gate.computing;
}
